import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { App as AntdApp, ConfigProvider } from 'antd';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Athletes from './pages/Athletes';
import Results from './pages/Results';
import AthleteResults from './pages/AthleteResults';
import Profile from './pages/Profile';
import LiveCompetitionSetup from './pages/LiveCompetitionSetup';
import Live from './pages/Live';
import Admin from './pages/Admin';
import ScrapeUrls from './pages/ScrapeUrls';
import Chat from './pages/Chat';
import ClubManagement from './pages/ClubManagement';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Layout from './components/Layout';
import PublicPageShell from './components/PublicPageShell';
import CookieConsentBanner from './components/CookieConsentBanner';
import './App.css';

/** Wraps public routes with footer shell. */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PublicPageShell>{children}</PublicPageShell>
);

const App: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: 'var(--blue-bell)',
          colorInfo: 'var(--blue-bell)',
          colorSuccess: 'var(--status-live)',
          colorWarning: 'var(--status-warning)',
          colorError: 'var(--status-danger)',
          colorLink: 'var(--blue-bell)',
          colorBgBase: 'var(--surface-page)',
          colorBgContainer: 'var(--surface-card)',
          colorBgElevated: '#ffffff',
          colorTextBase: 'var(--black)',
          colorBorder: 'var(--border-subtle)',
          colorBorderSecondary: 'var(--border-subtle)',
          borderRadius: 6,
          boxShadow: '0 1px 2px rgba(42, 40, 37, 0.05)',
          boxShadowSecondary: '0 1px 4px rgba(42, 40, 37, 0.04)',
        },
        components: {
          Input: {
            colorBgContainer: '#ffffff',
            activeBg: '#ffffff',
            hoverBg: '#ffffff',
          },
          Select: {
            selectorBg: '#ffffff',
          },
          DatePicker: {
            activeBg: '#ffffff',
          },
          Menu: {
            itemSelectedBg: 'rgba(92, 122, 143, 0.12)',
            itemSelectedColor: 'var(--text-primary)',
            itemHoverBg: 'var(--interactive-hover-bg)',
            itemHoverColor: 'var(--text-primary)',
            itemColor: 'var(--text-secondary)',
          },
          Table: {
            rowHoverBg: 'var(--interactive-hover-bg)',
            rowSelectedBg: 'var(--pale-sky)',
            rowSelectedHoverBg: 'var(--pale-sky)',
            headerSortActiveBg: 'var(--pale-sky)',
            headerSortHoverBg: 'var(--pale-sky)',
            fixedHeaderSortActiveBg: 'var(--pale-sky)',
          },
          Layout: {
            headerBg: 'var(--surface-page)',
            bodyBg: 'var(--surface-page)',
            siderBg: 'var(--surface-page)',
            triggerBg: 'var(--surface-page)',
          },
          Tabs: {
            /** Replace 32px left-only gutter with symmetric padding on each tab so labels center and active fill includes both sides. */
            horizontalItemGutter: 0,
            horizontalItemPadding: '12px 16px',
            horizontalItemPaddingSM: '8px 16px',
            horizontalItemPaddingLG: '16px 16px',
          },
          Tag: {
            defaultBg: 'var(--surface-card)',
            colorSuccessBg: 'rgba(90, 143, 110, 0.14)',
            colorSuccessBorder: 'rgba(90, 143, 110, 0.4)',
            colorSuccessText: 'var(--status-live)',
            colorPrimaryBg: 'rgba(92, 122, 143, 0.12)',
            colorPrimaryBorder: 'rgba(92, 122, 143, 0.35)',
            colorPrimaryText: 'var(--blue-bell)',
            colorErrorBg: 'rgba(184, 92, 82, 0.12)',
            colorErrorBorder: 'rgba(184, 92, 82, 0.35)',
            colorErrorText: 'var(--status-danger)',
            colorWarningBg: 'rgba(196, 154, 74, 0.14)',
            colorWarningBorder: 'rgba(196, 154, 74, 0.4)',
            colorWarningText: 'var(--status-warning)',
          },
        },
      }}
    >
      <AntdApp>
        <AuthProvider>
          <Router>
            <div className="App">
              <CookieConsentBanner />
              <Routes>
                <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/signup/*" element={<Navigate to="/login" replace />} />
                <Route path="/privacy" element={<PublicRoute><PrivacyPolicy /></PublicRoute>} />
                <Route path="/terms" element={<PublicRoute><TermsOfService /></PublicRoute>} />
                {/* Public live results page — no auth required */}
                <Route path="/live" element={<PublicRoute><Live /></PublicRoute>} />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                  <Route path="competitions" element={<Results />} />
                  <Route path="stats" element={<Navigate to="/dashboard" replace />} />
                  <Route path="results" element={<AthleteResults />} />
                  <Route path="athletes" element={<Athletes />} />
                  <Route path="chat" element={<Chat />} />
                  <Route path="competition-setup" element={<LiveCompetitionSetup />} />
                  <Route path="club-management" element={
                    <ProtectedRoute requireClaim="clubOwner">
                      <ClubManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="profile" element={<Profile />} />
                  <Route path="scrape-urls" element={<ScrapeUrls />} />
                  {/* Admin panel — admin claim required */}
                  <Route path="admin" element={
                    <ProtectedRoute requireClaim="admin">
                      <Admin />
                    </ProtectedRoute>
                  } />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;